
import { Loader2, MapPin } from "lucide-react";
import { useEffect, useState } from "react";

interface LocationButtonStateProps {
  isLoading: boolean;
  permission: "granted" | "denied" | "prompt";
  initialized?: boolean;
  hasCoords?: boolean;
  isResetting?: boolean;
}

export const LocationButtonState = ({
  isLoading,
  permission,
  initialized = true,
  hasCoords = false,
  isResetting = false
}: LocationButtonStateProps) => {
  // Add state to stabilize UI transitions and prevent bouncing
  const [stableState, setStableState] = useState<string>('initializing');
  
  // Use effect with debounce to stabilize state transitions
  useEffect(() => {
    // Determine the current logical state
    let newState = 'initializing';
    
    if (isResetting) {
      newState = 'resetting';
    } else if (!initialized) {
      newState = 'initializing';
    } else if (isLoading) {
      newState = 'loading';
    } else if (permission === 'granted') {
      newState = hasCoords ? 'refresh' : 'get';
    } else if (permission === 'denied') {
      newState = 'denied';
    } else {
      newState = 'enable';
    }
    
    // Use timeout to debounce state changes - increased to 400ms for better stability
    const timerId = setTimeout(() => {
      setStableState(newState);
    }, 400);
    
    return () => clearTimeout(timerId);
  }, [isLoading, permission, initialized, hasCoords, isResetting]);
  
  // Handle initializing state
  if (stableState === 'initializing') {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Initializing...
      </>
    );
  }
  
  // Handle resetting state
  if (stableState === 'resetting') {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Resetting...
      </>
    );
  }
  
  // Handle loading state
  if (stableState === 'loading') {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Updating...
      </>
    );
  }

  // Handle states based on stable permission value
  switch (stableState) {
    case 'refresh':
      return (
        <>
          <MapPin className="mr-2 h-4 w-4 text-green-500" />
          Refresh location
        </>
      );
    case 'get':
      return (
        <>
          <MapPin className="mr-2 h-4 w-4 text-green-500" />
          Get location
        </>
      );
    case 'denied':
      return (
        <>
          <MapPin className="mr-2 h-4 w-4 text-destructive" />
          Enable in settings
        </>
      );
    case 'enable':
    default:
      return (
        <>
          <MapPin className="mr-2 h-4 w-4" />
          Enable location
        </>
      );
  }
};
